import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import range from 'lodash/range';
import { clear, get, set } from 'idb-keyval';

import { useAuth, useFrami, usePrice, CompositionTemplateContext } from '../context';
import { calculateWallHeight, calculateWallWidth } from '../utilities/helpers';
import { calculateConversion } from '../data/gtm';
import { useTracking } from '../data/gtm';

import {
    getTemplateCategoryId,
    getTemplateClusterId,
    getTemplateFrameArea,
    getTemplateFrameCount,
    getTemplateHeight,
    getTemplateId,
    getTemplateProfileLength,
    getTemplateWidth,
} from '../utilities/template';
import { sortFramesByPosition } from '../utilities/composition';
import {
    assignFrameCountGroup,
    convertTemplateToFrames,
    getLUTFiltercategory,
    hasMargin,
} from '../utilities/compositionTemplate';
import { logError } from '../utilities/logError';

export const flipX = 'X';
export const flipY = 'Y';
export const flipXY = 'XY';

const CLUSTER_VERSION = 'v8.1';

const FEED_BESTSELLER_CATEGORIES = ["Tetris", "Organic", "Grid", "Vertical Brick"]

function isNotEmpty(obj) {
  return obj && Object.keys(obj).length > 0;
}

export function CompositionTemplateProvider(props) {
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [templates, setTemplates] = useState([]);

    const intl = useIntl();
    const { user, eventsCounter, setEventsCounter } = useAuth();
    const {
        currentFrami,
        memoizedMaterials,
        memoizedPapers,
        memoizedCardboardPassepartouts,
        memoizedCategories,
        memoizedFrameSizes,
        memoizedImageFilters,
        memoizedPredefinedFrameStyles,
    } = useFrami();

    const [passepartoutPreset, setPassepartoutPreset] = useState('STANDARD');
    const {
        assignMaterialsToFrames,
        autoDiscount,
        baseFramePrice,
        baseHangerPrice,
        currencyRates,
        getAutoDiscountPercentage,
        getPricePLN,
        getCompositionFramePrice,
    } = usePrice();
    const { pushEvent } = useTracking();

    const [compositionTemplatesLoading, setCompositionTemplatesLoading] = useState(false);
    const [compositionTemplatesLoadingProgress, setCompositionTemplatesLoadingProgress] = useState(0);

    const compositionTemplatesIndexRef = useRef(null);
    const filteredTemplatesRef = useRef([]);

    const [sortingCriterion, setSortingCriterion] = useState('frameAreaDescending');

    const [filteredOutCount, setFilteredOutCount] = useState({
        filterCategoryId: 0,
        filterPrice: 0,
        filterSize: 0,
        filterFrameCounts: 0,
    });

    const [filterWallSize, setFilterWallSize] = useState({
        width: calculateWallWidth(currentFrami?.wall_shape),
        height: calculateWallHeight(currentFrami?.wall_shape),
    });

    const [filterCategoryId, setFilterCategoryId] = useState(null);
    const [filterCategoryAvailableCounts, setFilterCategoryAvailableCounts] = useState({});

    const [filterPriceCurrency, setFilterPriceCurrency] = useState({ min: 0, max: Infinity });
    const [filterPriceApplied, setFilterPriceApplied] = useState(false);
    const [filterAvailablePriceCurrency, setFilterAvailablePriceCurrency] = useState({ min: Infinity, max: 0 });

    const [filterFrameCounts, setFilterFrameCounts] = useState([]);
    const [filterAvailableFrameCounts, setFilterAvailableFrameCounts] = useState(range(1, 26));
    const [filterAllFrameCounts, setFilterAllFrameCounts] = useState([]);

    const [currentPage, setCurrentPage] = useState(1); // Current page number
    const [numPages, setNumPages] = useState(1); // Number of pages
    const [filteredTemplatesCount, setFilteredTemplatesCount] = useState(null); // Number of templates after filtering
    const [templatesPerPage, setTemplatesPerPage] = useState(5*3); // Number of templates displayed per page
    const [templatesPaginated, setTemplatesPaginated] = useState({});
    const [templatePricePerM2, setTemplatePricePerM2] = useState(0);

    const [flipComposition, setFlipComposition] = useState(false);

    const [scrollPosition, setScrollPosition] = useState(0);

    const [isLoading, setIsLoading] = useState(false);
    const [isCategoryStairs, setIsCategoryStairs] = useState(false);
    // const loadedClusters = useRef({});
    const clusterPromises = useRef({});

    useEffect(() => {
        setCurrentTemplate(null);
    }, [currentFrami?.id]);

    const loadTemplatesIndex = useCallback(async () => {
        console.log('loadTemplatesIndex');

        const indexVersion = await get('compositionTemplateIndexVersion');
        if (indexVersion && indexVersion === CLUSTER_VERSION) {
            const index = await get('${passepartoutPreset}_compositionTemplateIndex');

            if (index) {
                compositionTemplatesIndexRef.current = index;
                _filterData(
                    currentFrami,
                    filterCategoryId,
                    filterFrameCounts,
                    filterPriceCurrency,
                    filterPriceApplied,
                    filterWallSize,
                    sortingCriterion,
                    templatesPerPage,
                );
                return;
            }
        } else {
            console.log('CompositionTemplate index version mismatch, clearing cache');
            clear();
        }

        setCompositionTemplatesLoading(true);

        const xhr = new XMLHttpRequest();

        xhr.open(
            'GET',
            `https://frami.fra1.cdn.digitaloceanspaces.com/compositiontemplate_clusters/${CLUSTER_VERSION}/${passepartoutPreset}/composition_templates.bin`,
            true
        );
        xhr.responseType = 'arraybuffer';

        xhr.onprogress = function (event) {
            if (event.lengthComputable) {
                const percentComplete = (event.loaded / event.total) * 100;
                console.log(`CompositionTemplate index download ${percentComplete}% complete`);
                setCompositionTemplatesLoadingProgress(percentComplete);
            }
        };

        xhr.onload = async function (event) {
            if (xhr.status === 200) {
                const uint32Array = new Uint32Array(xhr.response);

                compositionTemplatesIndexRef.current = uint32Array;
                await set(`${passepartoutPreset}_compositionTemplateIndex`, uint32Array);
                _filterData(
                    currentFrami,
                    filterCategoryId,
                    filterFrameCounts,
                    filterPriceCurrency,
                    filterPriceApplied,
                    filterWallSize,
                    sortingCriterion,
                    templatesPerPage,
                );

                console.log('loaded compositionTemplatesIndex');
                await set(`compositionTemplateIndexVersion`, CLUSTER_VERSION);
            } else {
                console.log(`Error ${xhr.status}: ${xhr.statusText}`);
            }
            setCompositionTemplatesLoading(false);
        };

        xhr.onerror = function () {
            console.log('Request failed');
            setCompositionTemplatesLoading(false);
        };

        xhr.send();
    }, [
        currentFrami,
        filterCategoryId,
        filterFrameCounts,
        filterPriceCurrency,
        filterPriceApplied,
        filterWallSize,
        sortingCriterion,
        templatesPerPage,
        currentPage,
        passepartoutPreset,
    ]);

    const unloadTemplatesIndex = () => {
        console.log('unloadTemplatesIndex');
        replaceTemplates([]);
        compositionTemplatesIndexRef.current = null;
    };

    useEffect(() => {
        loadTemplatesIndex();
    }, [passepartoutPreset]);

    useEffect(() => {
        if (memoizedCategories instanceof Map) {
            const category = memoizedCategories.get(filterCategoryId);
            if (category?.name === 'Stairs') {
                setIsCategoryStairs(true);
            } else {
                setIsCategoryStairs(false);
            }
        }
    }, [filterCategoryId, memoizedCategories]);

    // Define sorting constants
    const FRAME_COUNT_ASCENDING = 'frameCountAscending';
    const FRAME_COUNT_DESCENDING = 'frameCountDescending';
    const PRICE_ASCENDING = 'priceAscending';
    const PRICE_DESCENDING = 'priceDescending';
    const FRAME_AREA_DESCENDING = 'frameAreaDescending';

    // Use the constants in your sort function
    const sortTemplates = (templateIndices, criterion) => {
        return templateIndices.sort((a_index, b_index) => {
            switch (criterion) {
                case FRAME_COUNT_ASCENDING:
                    return (
                        getTemplateFrameCount(a_index, compositionTemplatesIndexRef.current) -
                        getTemplateFrameCount(b_index, compositionTemplatesIndexRef.current)
                    );
                case FRAME_COUNT_DESCENDING:
                    return (
                        getTemplateFrameCount(b_index, compositionTemplatesIndexRef.current) -
                        getTemplateFrameCount(a_index, compositionTemplatesIndexRef.current)
                    );
                case PRICE_ASCENDING:
                    return calculateTemplatePrice(a_index) - calculateTemplatePrice(b_index);
                case PRICE_DESCENDING:
                    return calculateTemplatePrice(b_index) - calculateTemplatePrice(a_index);
                case FRAME_AREA_DESCENDING:
                default:
                    return (
                        getTemplateFrameArea(b_index, compositionTemplatesIndexRef.current) -
                        getTemplateFrameArea(a_index, compositionTemplatesIndexRef.current)
                    );
            }
        });
    };

    const handleSetSortingCriterion = (criterion) => {        
        setSortingCriterion(criterion);
        pushEvent('template_sorting', { sorting: sortingCriterion });
    };

    const sortOptions = useMemo(
        () => [
            {
                id: FRAME_AREA_DESCENDING,
                text: intl.formatMessage({ id: 'studio.composition.type.sort.frame_area_descending' }),
                onClick: () => setSortingCriterion(FRAME_AREA_DESCENDING),
                selected: FRAME_AREA_DESCENDING === sortingCriterion,
                dataCy: 'option-default',
            },
            {
                id: FRAME_COUNT_ASCENDING,
                text: intl.formatMessage({ id: 'studio.composition.type.sort.frame_count_ascending' }),
                onClick: () => setSortingCriterion(FRAME_COUNT_ASCENDING),
                selected: FRAME_COUNT_ASCENDING === sortingCriterion,
                dataCy: 'option-frame-ascending',
            },
            {
                id: FRAME_COUNT_DESCENDING,
                text: intl.formatMessage({ id: 'studio.composition.type.sort.frame_count_descending' }),
                onClick: () => setSortingCriterion(FRAME_COUNT_DESCENDING),
                selected: FRAME_COUNT_DESCENDING === sortingCriterion,
                dataCy: 'option-frame-descending',
            },
            {
                id: PRICE_ASCENDING,
                text: intl.formatMessage({ id: 'studio.composition.type.sort.price_ascending' }),
                onClick: () => setSortingCriterion(PRICE_ASCENDING),
                selected: PRICE_ASCENDING === sortingCriterion,
                dataCy: 'option-price-ascending',
            },
            {
                id: PRICE_DESCENDING,
                text: intl.formatMessage({ id: 'studio.composition.type.sort.price_descending' }),
                onClick: () => setSortingCriterion(PRICE_DESCENDING),
                selected: PRICE_DESCENDING === sortingCriterion,
                dataCy: 'option-price-descending',
            },
        ],
        [sortingCriterion]
    );

    const calculateTemplatePrice = useCallback(
        (
            index,
            profilePricePerMPLN,
            paperPricePerM2PLN,
            hanging_system,
            passepartoutPricePerM2PLN = 0,
            framesWithMaterials = null
        ) => {
            const profileLength = getTemplateProfileLength(index, compositionTemplatesIndexRef.current);
            const frameArea = getTemplateFrameArea(index, compositionTemplatesIndexRef.current);
            const width = getTemplateWidth(index, compositionTemplatesIndexRef.current);
            const height = getTemplateHeight(index, compositionTemplatesIndexRef.current);
            const frameCount = getTemplateFrameCount(index, compositionTemplatesIndexRef.current);

            if (!profilePricePerMPLN) {
                const material = memoizedMaterials.get(currentFrami?.default_frame_material);
                profilePricePerMPLN = material?.price_per_m || 20;
            }
            if (!paperPricePerM2PLN) {
                const paper = memoizedPapers.get(currentFrami?.default_paper);
                paperPricePerM2PLN = paper?.price_per_m2 || 250;
            }
            if (!hanging_system) {
                hanging_system = currentFrami?.default_hanging_system || 'M';
            }

            const basePrice = frameCount * baseFramePrice;
            let pricePLNFramesBrutto = ((profileLength / 100) * profilePricePerMPLN + basePrice) * 1.23;

            if (framesWithMaterials && currentFrami) {
                pricePLNFramesBrutto = getCompositionFramePrice(framesWithMaterials);
            }

            const pricePLNPrintsBrutto = (frameArea / 1000000) * paperPricePerM2PLN * 1.23;
            const pricePLNPassepartoutBrutto = (frameArea / 1000000) * passepartoutPricePerM2PLN * 1.23;

            let pricePLNTemplateBrutto = 0;
            let pricePLNHangersBrutto = 0;

            if (hanging_system !== 'F') {
                pricePLNTemplateBrutto = ((width * height) / 1000000) * templatePricePerM2 * 1.23;
            }
            if (hanging_system === 'T') {
                pricePLNHangersBrutto = baseHangerPrice * frameCount * 1.23;
            }

            const pricePLNBrutto =
                pricePLNFramesBrutto +
                pricePLNPrintsBrutto +
                pricePLNTemplateBrutto +
                pricePLNHangersBrutto +
                pricePLNPassepartoutBrutto;

            return pricePLNBrutto;
        },
        [getCompositionFramePrice, templatePricePerM2, baseFramePrice, baseHangerPrice]
    );

    const findRandomTemplateIndex = async (frameCount, wallSize) => {
        if (frameCount < 3) {
            frameCount = 3;
        }
        let results = [];

        const index = compositionTemplatesIndexRef.current;
        function findTemplateByFrameCount(frameCount) {
            for (let i = 0; i < index.length / 9; i++) {
                // const templateCategoryId = getTemplateCategoryId(i, index)
                const templateFrameCount = getTemplateFrameCount(i, index)
                const templateWidth = getTemplateWidth(i, index)
                const templateHeight = getTemplateHeight(i, index)
                if (
                    // templateCategoryId === categoryId &&
                    templateFrameCount === frameCount &&
                    templateWidth <= wallSize.width &&
                    templateHeight <= wallSize.height
                ) {
                    results.push(i)
                }
            }
        }

        findTemplateByFrameCount(frameCount);

        if (results.length === 0) {
            findTemplateByFrameCount(frameCount - 1);
        }
        if (results.length === 0) {
            findTemplateByFrameCount(frameCount - 2);
        }

        const sortedTemplates = sortTemplates(results, 'frameAreaDescending');
        const templateIndex = sortedTemplates[0];
        return templateIndex;
    }

    const _filterData = useCallback(
        async (
            currentFrami,
            filterCategoryId,
            filterFrameCounts,
            filterPriceCurrency,
            filterPriceApplied,
            filterWallSize,
            sortingCriterion,
            templatesPerPage,
        ) => {

            const startTime = performance.now();

            if (
                !compositionTemplatesIndexRef.current ||
                compositionTemplatesIndexRef.current.length === 0 ||
                !memoizedMaterials ||
                !filterPriceCurrency
            ) {
                console.log(
                    'Skipping filterData',
                    compositionTemplatesIndexRef.current?.length,
                    memoizedMaterials,
                    filterPriceCurrency
                );
                return;
            }

            const availableFrameCountsSet = new Set();
            const allFrameCountsSet = new Set();

            const material = memoizedMaterials.get(currentFrami?.default_frame_material);
            const paper = memoizedPapers.get(currentFrami?.default_paper);
            const cardboardPassepartout =
                currentFrami?.default_passepartout_width !== 'None'
                    ? memoizedCardboardPassepartouts.get(currentFrami?.default_cardboard_passepartout)
                    : null;

            const filterFrameCountsSet = new Set(filterFrameCounts);

            const filterPriceAvailablePLN = { min: Infinity, max: 0 };
            const currencyRate = currentFrami ? currencyRates[currentFrami.currency] : 1;

            const filterPricePLN = {
                min: filterPriceCurrency.min * currencyRate,
                max: filterPriceCurrency.max * currencyRate,
            };

            const filteredOut = { filterCategoryId: 0, filterPrice: 0, filterSize: 0, filterFrameCounts: 0 };

            filteredTemplatesRef.current = [];

            const templateCountPerCategory = {};

            for (let index = 0; index < compositionTemplatesIndexRef.current.length / 9; index++) {
                allFrameCountsSet.add(getTemplateFrameCount(index, compositionTemplatesIndexRef.current));
                const categoryId = getTemplateCategoryId(index, compositionTemplatesIndexRef.current);

                var filter = true;
                var filterApartCategory = true;
                if (filterCategoryId && !(categoryId === filterCategoryId)) {
                    filter = false;
                    filteredOut.filterCategoryId += 1;
                }
                const w = getTemplateWidth(index, compositionTemplatesIndexRef.current);
                const h = getTemplateHeight(index, compositionTemplatesIndexRef.current);
                const category = memoizedCategories.get(categoryId);

                if (!(w <= filterWallSize.width && h <= filterWallSize.height)) {
                    filter = false;
                    filterApartCategory = category?.name === 'Stairs';
                    filteredOut.filterSize += 1;
                }

                const price = Math.round(
                    calculateTemplatePrice(
                        index,
                        material?.price_per_m,
                        paper?.price_per_m2,
                        currentFrami?.default_hanging_system,
                        cardboardPassepartout?.price_per_m2,
                    )
                );

                if (filter) {
                    if (price > filterPriceAvailablePLN.max) filterPriceAvailablePLN.max = price;
                    if (price < filterPriceAvailablePLN.min) filterPriceAvailablePLN.min = price;
                }

                if (filterPriceApplied && filterPriceCurrency) {
                    if (filterPriceApplied && !(price >= filterPricePLN.min && price <= filterPricePLN.max)) {
                        filter = false;
                        filterApartCategory = false;
                        filteredOut.filterPrice += 1;
                    }
                }
                if (filter) {
                    availableFrameCountsSet.add(getTemplateFrameCount(index, compositionTemplatesIndexRef.current));
                }
                if (
                    filterFrameCounts &&
                    filterFrameCounts.length > 0 &&
                    !filterFrameCountsSet.has(getTemplateFrameCount(index, compositionTemplatesIndexRef.current))
                ) {
                    filter = false;
                    filterApartCategory = false;

                    filteredOut.filterFrameCounts += 1;
                }
                if (filter) {
                    filteredTemplatesRef.current.push(index);
                }
                if (filterApartCategory) {
                    templateCountPerCategory[categoryId] = (templateCountPerCategory[categoryId] || 0) + 1;
                }
            }

            const priceAvailable = {
                min: parseInt(filterPriceAvailablePLN.min / currencyRate),
                max: parseInt(filterPriceAvailablePLN.max / currencyRate),
            };

            setFilterAvailablePriceCurrency(priceAvailable);

            setFilterPriceCurrency(filterPriceCurrency);
            setFilteredOutCount(filteredOut);

            setFilterAvailableFrameCounts(Array.from(availableFrameCountsSet).sort((a, b) => a - b));
            setFilterAllFrameCounts(Array.from(allFrameCountsSet).sort((a, b) => a - b));

            const sortedTemplates = sortTemplates(filteredTemplatesRef.current, sortingCriterion);

            setFilteredTemplatesCount(sortedTemplates.length);
            const totalPages = Math.min(Math.ceil(sortedTemplates.length / templatesPerPage), 100);
            setNumPages(totalPages);

            // Create paginated results object
            const paginatedResults = {};
            for (let page = 1; page <= totalPages; page++) {
                const indexOfLastTemplate = page * templatesPerPage;
                const indexOfFirstTemplate = indexOfLastTemplate - templatesPerPage;
                paginatedResults[page] = sortedTemplates.slice(indexOfFirstTemplate, indexOfLastTemplate);
            }
            // console.log('paginateResults')
            setTemplatesPaginated(paginatedResults);

            // Get current page results
            // const results = paginatedResults[currentPage] || [];

            // replaceTemplates(results);

            setFilterCategoryAvailableCounts(templateCountPerCategory);
            const endTime = performance.now();
            console.log('Filtering took', endTime - startTime, 'ms');
        },
        [
            compositionTemplatesLoading,
            currentFrami,
            memoizedMaterials,
            calculateTemplatePrice,
            sortTemplates,
            autoDiscount,
            currencyRates,
        ]
    );

    useEffect(() => {
        if (!compositionTemplatesIndexRef.current) return;
        if (!currentFrami) return;
        if (filterWallSize.width === 0 || filterWallSize.height === 0) return;
        if (!isNotEmpty(currencyRates)) return;

        console.log('useEffect', currencyRates, templatesPerPage, compositionTemplatesLoading, filterWallSize)

        setIsLoading(true);
        let timerId;

        timerId = setTimeout(() => {
            _filterData(
                currentFrami,
                filterCategoryId,
                filterFrameCounts,
                filterPriceCurrency,
                filterPriceApplied,
                filterWallSize,
                sortingCriterion,
                templatesPerPage,
            );
            setIsLoading(false);
        }, 10);

        // Clear timeout if the component unmounts before the timeout has run out
        return () => clearTimeout(timerId);
    }, [
        currentFrami,
        filterCategoryId,
        filterFrameCounts,
        filterWallSize,
        filterPriceApplied,
        filterPriceCurrency,
        sortingCriterion,
        // currentPage,
        compositionTemplatesLoading,
        // autoDiscount,
        currencyRates,
        templatesPerPage
    ]);

    useEffect(() => {
        setCurrentPage(1);
    }, [
        filterCategoryId,
        filterFrameCounts,
        filterWallSize,
        filterPriceApplied,
        filterPriceCurrency,
        passepartoutPreset,
    ]);

    const setCurrentTemplateWithEvents = async (index) => {
        if (index) {
            const template = await getCompositionTemplate(index);
            if (template) {
                const category = memoizedCategories.get(template.categoryId);
                const priceInPln = Math.round(getPricePLN(template.price, currentFrami.currency));
                const updatedEventsCount = (eventsCounter['composition_select'] || 0) + 1;
                setEventsCounter((prev) => ({ ...prev, composition_select: updatedEventsCount }));

                const lutFilterCategory = getLUTFiltercategory(memoizedImageFilters.get(currentFrami.default_lut_filter))
                const framesCountGroup = assignFrameCountGroup(template.frameCount);
                const margin = hasMargin(currentFrami.default_passepartout_width)

                pushEvent('composition_select', {
                    templateId: template.id,
                    category: category?.name ?? 'All',
                    price: Math.round(template.price),
                    price_in_pln: priceInPln,
                    value:
                        Math.round(
                            calculateConversion('composition_select', updatedEventsCount) * template.price * 100
                        ) / 100,
                    currency: currentFrami.currency,
                    frameCount: template.frameCount,
                    width: template.width,
                    height: template.height,
                });
                pushEvent('view_item',  {
                    currency: currentFrami.currency,
                    value: Math.round((template.price / 1.23) * 100) / 100,
                    items: [
                        {
                            item_id: generate_sku(category?.name, lutFilterCategory, framesCountGroup, margin),
                            item_brand: "Framky",
                            item_category: category?.name,
                            item_category2: lutFilterCategory,
                            item_category3: framesCountGroup,
                            item_category4: margin,
                            item_category5: currentFrami.default_predefined_frame_style,
                            quantity: 1,
                        }
                    ],
                    price_in_pln: priceInPln,
                    templateId: template.id,
                });

                setCurrentTemplate(template);
            }
        } else {
            setCurrentTemplate(null);
        }
    };

    const generate_sku = (category, lutFilterCategory, framesCountGroup, margin) => {
        if (category) {
            if (!FEED_BESTSELLER_CATEGORIES.includes(category)) {
                return `${category.toUpperCase()}_${lutFilterCategory}`
            } else {
                return `${category.toUpperCase()}_${lutFilterCategory}_${framesCountGroup}_${margin}`
            }
        }
        return 'CUSTOM'
    }

    const replaceTemplates = (newTemplates) => {
        setTemplates(newTemplates);
    };

    useEffect(() => {
        if (currentFrami) {
            let width, height;
            if (isCategoryStairs) {
                width = calculateWallWidth(currentFrami.stairs_wall_shape);
                height = calculateWallHeight(currentFrami.stairs_wall_shape);
            } else {
                width = calculateWallWidth(currentFrami.wall_shape);
                height = calculateWallHeight(currentFrami.wall_shape);
            }
            setFilterWallSize({ width, height });
        }
    }, [currentFrami?.wall_shape, currentFrami?.stairs_wall_shape, isCategoryStairs]);

    useEffect(() => {
        if (memoizedPapers) {
            const template = memoizedPapers.get('TEMPLATE');
            if (template) {
                setTemplatePricePerM2(template?.price_per_m2); // Price in PLN
            }
        } else {
            console.log('memoizedPapers is not set', memoizedPapers);
        }
    }, [memoizedPapers]);

    const getCompositionTemplateID = (templateIndex) => {
        const compositionTemplateId = getTemplateId(templateIndex, compositionTemplatesIndexRef.current);
        return compositionTemplateId;
    }

    const prepareCompositionTemplate = useCallback(
        (index, cluster) => {
            // const startTime = performance.now();
            const compositionTemplateId = getTemplateId(index, compositionTemplatesIndexRef.current);
            const clusterId = getTemplateClusterId(index, compositionTemplatesIndexRef.current);

            const profileLength = getTemplateProfileLength(index, compositionTemplatesIndexRef.current);
            const frameArea = getTemplateFrameArea(index, compositionTemplatesIndexRef.current);
            const width = getTemplateWidth(index, compositionTemplatesIndexRef.current);
            const height = getTemplateHeight(index, compositionTemplatesIndexRef.current);
            const frameCount = getTemplateFrameCount(index, compositionTemplatesIndexRef.current);

            const frames = cluster[compositionTemplateId];
            if (!frames) {
                console.log("Couldn't find template ", compositionTemplateId, ' in cluster ', clusterId);
                return null;
            }

            const newFrames = Object.keys(frames).reduce((acc, key) => {
                const framePositions = frames[key];
                const frameSize = memoizedFrameSizes.get(key);
                const newFramePositions = framePositions.map((position) => {
                    if (flipComposition === flipX) {
                        return [width / 200 - frameSize.width - position[0], position[1]];
                    } else if (flipComposition === flipY) {
                        return [position[0], -height / 200 + frameSize.height - position[1]];
                    } else if (flipComposition === flipXY) {
                        return [
                            -width / 200 - frameSize.width - position[0],
                            -height / 200 + frameSize.height - position[1],
                        ];
                    } else {
                        return position;
                    }
                });
                acc[key] = newFramePositions;
                return acc;
            }, {});

            const template = {
                id: getTemplateId(index, compositionTemplatesIndexRef.current),
                profileLength,
                width,
                height,
                frameArea,
                frameCount,
                categoryId: getTemplateCategoryId(index, compositionTemplatesIndexRef.current),
                frames: newFrames,
                templateIndex: index,
            };

            const material = memoizedMaterials.get(currentFrami.default_frame_material);
            const paper = memoizedPapers.get(currentFrami.default_paper);
            const cardboardPassepartout =
                currentFrami.default_passepartout_width !== 'None'
                    ? memoizedCardboardPassepartouts.get(currentFrami.default_cardboard_passepartout)
                    : null;
            const frameStyle = memoizedPredefinedFrameStyles.get(currentFrami.default_predefined_frame_style);

            const framesToCalculate = convertTemplateToFrames(template, memoizedFrameSizes);
            sortFramesByPosition(framesToCalculate);
            let recalculatedFrames;
            if (frameStyle && frameStyle.frame_materials.length > 1) {
                const frameMaterials = assignMaterialsToFrames(frameStyle, framesToCalculate);
                recalculatedFrames = framesToCalculate.map((frame) => {
                    return {
                        ...frame,
                        frame_material: frameMaterials[frame.id],
                    };
                });
            }

            const pricePLNBrutto = calculateTemplatePrice(
                index,
                material?.price_per_m,
                paper?.price_per_m2,
                currentFrami.default_hanging_system,
                cardboardPassepartout?.price_per_m2,
                recalculatedFrames
            );

            const currency = currentFrami.currency;

            template.price = pricePLNBrutto / currencyRates[currency];
            // const endTime = performance.now();
            // console.log('Preparing template took', endTime - startTime, 'ms');
            return template;
        },
        [
            flipComposition,
            currentFrami,
            memoizedCardboardPassepartouts,
            memoizedFrameSizes,
            memoizedMaterials,
            memoizedPapers,
            memoizedPredefinedFrameStyles,
            calculateTemplatePrice,
            currencyRates,
        ]
    );

    const getAvgTemplatePrice = useCallback(
        (filterWallSize) => {
            if (
                !compositionTemplatesIndexRef.current ||
                compositionTemplatesIndexRef.current.length === 0 ||
                !memoizedMaterials ||
                !filterPriceCurrency
            ) {
                console.log(
                    'Skipping filterData',
                    compositionTemplatesIndexRef.current?.length,
                    memoizedMaterials,
                    filterPriceCurrency
                );
                return;
            }

            const material = memoizedMaterials.get(currentFrami?.default_frame_material);
            const paper = memoizedPapers.get(currentFrami?.default_paper);
            const cardboardPassepartout =
                currentFrami?.default_passepartout_width !== 'None'
                    ? memoizedCardboardPassepartouts.get(currentFrami?.default_cardboard_passepartout)
                    : null;

            const filterPriceAvailablePLN = { min: Infinity, max: 0 };

            filteredTemplatesRef.current = [];

            for (let index = 0; index < compositionTemplatesIndexRef.current.length / 9; index++) {
                if (
                    getTemplateWidth(index, compositionTemplatesIndexRef.current) <= filterWallSize.width &&
                    getTemplateHeight(index, compositionTemplatesIndexRef.current) <= filterWallSize.height
                ) {
                    const price = Math.round(
                        calculateTemplatePrice(
                            index,
                            material?.price_per_m,
                            paper?.price_per_m2,
                            currentFrami?.default_hanging_system,
                            cardboardPassepartout?.price_per_m2
                        )
                    );
                    if (price > filterPriceAvailablePLN.max) filterPriceAvailablePLN.max = price;
                    if (price < filterPriceAvailablePLN.min) filterPriceAvailablePLN.min = price;
                }
            }

            const currencyRate = currentFrami ? currencyRates[currentFrami.currency] : 1;

            const autoDiscountPercentage = getAutoDiscountPercentage(filterPriceAvailablePLN.max);

            return ((filterPriceAvailablePLN.max / 2) * (1 - autoDiscountPercentage / 100)) / currencyRate;
        },
        [currentFrami, currencyRates, autoDiscount]
    );

    const getCompositionTemplate = useCallback(
        async (index) => {
            const startTime = performance.now();

            if (!compositionTemplatesIndexRef.current) {
                return null;
            }

            const clusterId = getTemplateClusterId(index, compositionTemplatesIndexRef.current);

            const cluster = await get(`${passepartoutPreset}_cluster_${clusterId}`);

            if (cluster) {
                return prepareCompositionTemplate(index, cluster);
            } else {
                if (!clusterPromises[`${passepartoutPreset}_cluster_${clusterId}`]) {
                    clusterPromises[`${passepartoutPreset}_cluster_${clusterId}`] = (async () => {
                        try {
                            console.log(
                                'Loading cluster:',
                                `${CLUSTER_VERSION}/${passepartoutPreset}/cluster_${clusterId}.json`
                            );

                            const response = await fetch(
                                `https://frami.fra1.cdn.digitaloceanspaces.com/compositiontemplate_clusters/${CLUSTER_VERSION}/${passepartoutPreset}/cluster_${clusterId}.json`
                            );
                            const clusterData = await response.json();

                            await set(`${passepartoutPreset}_cluster_${clusterId}`, clusterData);

                            return clusterData;
                        } catch (err) {
                            console.error('Error loading composition template:', clusterId, err);
                            logError(err);
                        }
                    })();
                }

                const loaded_cluster = await clusterPromises[`${passepartoutPreset}_cluster_${clusterId}`];

                const result = prepareCompositionTemplate(index, loaded_cluster);
                const endTime = performance.now();
                console.log('Loading cluster took', endTime - startTime, 'ms');

                return loaded_cluster ? result : null;
            }
        },
        [prepareCompositionTemplate, passepartoutPreset]
    );

    const BASIC_TEMPLATE = {
        categoryId: 21,
        frameArea: 410000,
        frameCount: 4,
        frames: {
            B_15x20L: [
                [-225, -200],
                [25, 350],
            ],
            B_35x50: [
                [-375, 350],
                [25, 150],
            ],
        },
        height: 700,
        id: 3700666,
        price: 195,
        profileLength: 480,
        templateIndex: 197986,
        width: 750,
    };

    const value = {
        loadTemplatesIndex,
        unloadTemplatesIndex,
        BASIC_TEMPLATE,

        currentTemplate,
        templates,

        compositionTemplatesLoading,
        compositionTemplatesLoadingProgress,

        filterWallSize,

        filterCategoryId,
        setFilterCategoryId,
        filterCategoryAvailableCounts,
        isCategoryStairs,

        filteredOutCount,

        filterFrameCounts,
        filterAvailableFrameCounts,
        filterAllFrameCounts,
        setFilterFrameCounts,

        filterPriceCurrency,
        setFilterPriceCurrency,
        filterPriceApplied,
        setFilterPriceApplied,
        filterAvailablePriceCurrency,
        setFilterWallSize,

        flipComposition,
        setFlipComposition,

        setCurrentTemplate: setCurrentTemplateWithEvents,
        isFilteringTemplates: isLoading,
        getCompositionTemplate,
        getAvgTemplatePrice,

        filteredTemplatesCount,
        numPages,
        currentPage,
        templatesPerPage,
        setTemplatesPerPage,
        setCurrentPage,

        sortOptions,
        sortingCriterion,
        setSortingCriterion: handleSetSortingCriterion,

        setPassepartoutPreset,

        scrollPosition,
        setScrollPosition,
        generate_sku,
        findRandomTemplateIndex,
        getCompositionTemplateID,
        templatesPaginated
    };

    return <CompositionTemplateContext.Provider value={value} {...props} />;
}
